import React, { useEffect, useRef, useState } from "react";
import { GiCrystalBall, GiPrayer } from "react-icons/gi";
import { FiStar } from "react-icons/fi";
import OmSymbol from "./OmSymbol";

// Resolves a STATS `icon` key to a react-icon component
const STAT_ICONS = {
  flame: GiPrayer,
  om: OmSymbol,
  crystal: GiCrystalBall,
  star: FiStar,
};

/**
 * Counts a number up from 0 to `value` once it scrolls into view.
 * `value` should be numeric; `suffix`/`prefix` add symbols like + or ★.
 */
function CountUp({ value, suffix = "", prefix = "", duration = 1800 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const tick = (now) => {
              const elapsed = now - start;
              const progress = Math.min(elapsed / duration, 1);
              // easeOutExpo for a satisfying finish
              const eased = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
              setDisplay(value * eased);
              if (progress < 1) requestAnimationFrame(tick);
              else setDisplay(value);
            };
            requestAnimationFrame(tick);
          }
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [value, duration]);

  // Format: integers without decimals, ratings with 1 decimal
  const isDecimal = value % 1 !== 0;
  const formatted = isDecimal ? display.toFixed(1) : Math.round(display).toLocaleString("en-IN");

  return (
    <span ref={ref}>
      {prefix}
      {formatted}
      {suffix}
    </span>
  );
}

export default function StatsCounter({ stats, light = false }) {
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
      {stats.map((s) => {
        const Icon = STAT_ICONS[s.icon] || OmSymbol;
        return (
          <div
            key={s.label}
            className={`text-center rounded-2xl p-6 ${
              light
                ? "glass-card"
                : "bg-white border border-gold-100 shadow-premium hover-lift"
            }`}
          >
            <div className={`flex justify-center mb-2 ${light ? "text-gold-300" : "text-saffron-500"}`}>
              <Icon size={32} />
            </div>
            <div
              className={`font-heading text-3xl sm:text-4xl mb-1 ${
                light ? "text-gradient-gold" : "text-maroon-800"
              }`}
            >
              <CountUp value={s.value} suffix={s.suffix} prefix={s.prefix} />
            </div>
            <div className={`text-xs sm:text-sm font-medium ${light ? "text-gold-100/85" : "text-gray-600"}`}>
              {s.label}
            </div>
          </div>
        );
      })}
    </div>
  );
}
